import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    axios.get(`${API}/auth/reset-password/validate`, { params: { token } })
      .then(({ data }) => setValid(!!data.valid))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("A senha deve ter no mínimo 6 caracteres");
    if (pwd !== pwd2) return setError("As senhas não conferem");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 3500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background dark flex items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6" data-testid="reset-password-page">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center"><KeyRound size={20} className="text-white" /></div>
          <div>
            <p className="font-heading font-bold text-lg leading-none tracking-tight">ASTRAL</p>
            <p className="text-[10px] text-muted-foreground tracking-[0.22em] uppercase">Control Intelligence</p>
          </div>
        </div>

        {valid === null && <p className="text-sm text-muted-foreground">Validando link de redefinição...</p>}

        {valid === false && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 space-y-3" data-testid="reset-invalid">
            <p className="text-sm text-red-300 flex items-start gap-2"><AlertTriangle size={16} className="shrink-0 mt-0.5" />
              Este link de redefinição é inválido ou expirou. Solicite uma nova redefinição na tela de login.</p>
            <Link to="/login" className="text-sm text-primary hover:underline block">Voltar para o login</Link>
          </div>
        )}

        {valid && done && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 space-y-2" data-testid="reset-success">
            <p className="text-sm text-emerald-300 flex items-start gap-2"><CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              Senha redefinida com sucesso! Redirecionando para o login...</p>
            <Link to="/login" className="text-sm text-primary hover:underline block">Ir para o login agora</Link>
          </div>
        )}

        {valid && !done && (
          <>
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Redefinir senha</h2>
              <p className="text-sm text-muted-foreground mt-1.5">Crie uma nova senha para a sua conta. Todas as sessões anteriores serão encerradas.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nova senha (mín. 6 caracteres)</Label>
                <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-new-password" />
              </div>
              <div className="space-y-2">
                <Label>Confirmar nova senha</Label>
                <Input type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="reset-confirm-password" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                {loading ? "Redefinindo..." : "Redefinir senha"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
